/* Query and mutation hooks for Inventory — STORY-028, 029, 030.
 *
 * Every write invalidates the whole prefix, because the three screens read the
 * same rows from different angles: marking something returned changes the
 * still-out count, the Event's loadout, and the item's condition at once.
 * Writing one back and trusting the rest is how the same item comes to look
 * returned on one screen and out on another.
 */

import {
  type AssignItemsRequest,
  assignmentsResponseSchema,
  type CreateItemRequest,
  inventoryItemSchema,
  inventoryResponseSchema,
  outstandingResponseSchema,
  type ReturnItemsRequest,
  type UpdateItemRequest,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/api/client.js';

export const inventoryKey = (organisationId: string) =>
  ['organisations', organisationId, 'inventory'] as const;

export const loadoutKey = (organisationId: string, eventId: string) =>
  ['organisations', organisationId, 'events', eventId, 'equipment'] as const;

export const outstandingKey = (organisationId: string, timeZone: string) =>
  ['organisations', organisationId, 'equipment', 'outstanding', timeZone] as const;

const path = (organisationId: string) => `/organisations/${organisationId}/inventory`;

/**
 * Everything equipment, invalidated together.
 *
 * Not surgical, deliberately: a return changes the item's condition, the
 * Event's loadout and the outstanding count, and enumerating which keys those
 * are would be a second model of the relationships that could disagree with the
 * first.
 */
const refresh = (queryClient: ReturnType<typeof useQueryClient>, organisationId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: inventoryKey(organisationId) }),
    queryClient.invalidateQueries({
      queryKey: ['organisations', organisationId, 'equipment'] as const,
    }),
    queryClient.invalidateQueries({
      queryKey: ['organisations', organisationId, 'events'] as const,
    }),
  ]);

export function useInventory(organisationId: string) {
  return useQuery({
    queryKey: inventoryKey(organisationId),
    queryFn: () => request(path(organisationId), { schema: inventoryResponseSchema }),
    retry: false,
  });
}

export function useLoadout(organisationId: string, eventId: string) {
  return useQuery({
    queryKey: loadoutKey(organisationId, eventId),
    queryFn: () =>
      request(`/organisations/${organisationId}/events/${eventId}/equipment`, {
        schema: assignmentsResponseSchema,
      }),
    retry: false,
  });
}

/**
 * What has not come back.
 *
 * The reader's zone is part of the key because it decides which side of the
 * outstanding/packing split a row falls on — a cached answer computed for
 * another zone is a wrong answer, not a stale one.
 */
export function useOutstanding(organisationId: string, timeZone: string) {
  return useQuery({
    queryKey: outstandingKey(organisationId, timeZone),
    queryFn: () =>
      request(
        `/organisations/${organisationId}/equipment/outstanding?timeZone=${encodeURIComponent(timeZone)}`,
        { schema: outstandingResponseSchema },
      ),
    retry: false,
  });
}

export function useCreateItem(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateItemRequest) =>
      request(path(organisationId), { method: 'POST', schema: inventoryItemSchema, body: values }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

export function useUpdateItem(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, ...values }: UpdateItemRequest & { itemId: string }) =>
      request(`${path(organisationId)}/${itemId}`, {
        method: 'PATCH',
        schema: inventoryItemSchema,
        body: values,
      }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

export function useDeleteItem(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) =>
      request(`${path(organisationId)}/${itemId}`, { method: 'DELETE' }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

export function useAssignEquipment(organisationId: string, eventId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: AssignItemsRequest) =>
      request(`/organisations/${organisationId}/events/${eventId}/equipment`, {
        method: 'POST',
        schema: assignmentsResponseSchema,
        body: values,
      }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

export function useUnassignEquipment(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) =>
      request(`/organisations/${organisationId}/equipment/${assignmentId}`, { method: 'DELETE' }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

/**
 * One mutation for every screen that marks kit returned.
 *
 * Three entry points that behaved differently meant the condition was captured
 * or lost depending on which screen somebody happened to be on — found
 * 2026-08-11 by marking the same item returned from three places and getting
 * three different outcomes.
 */
export function useReturnEquipment(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: ReturnItemsRequest) =>
      request(`/organisations/${organisationId}/equipment/returns`, {
        method: 'POST',
        schema: assignmentsResponseSchema,
        body: values,
      }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}
